import { useCallback, useEffect, useRef } from "react";
import Image from "@Components/Image";
import ChevronLeftIcon from "@Icons/ChevronLeftIcon";
import ChevronRightIcon from "@Icons/ChevronRightIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { FloorPlanImage } from "../../utils/floorPlanHelpers";
import styles from "./ImageViewer.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

export interface ImageViewerProps {
  images: FloorPlanImage[];
  currentIndex: number;
  zoom?: { level: number };
  onNext: () => void;
  onPrev: () => void;
  altText: string;
  showChevrons?: boolean;
  onImageClick?: () => void;
}

const SWIPE_THRESHOLD = 50;

const ImageViewer = ({
  images,
  currentIndex,
  zoom = { level: 100 },
  onNext,
  onPrev,
  altText,
  showChevrons = true,
  onImageClick,
}: ImageViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const hasMultipleImages = images.length > 1 && showChevrons;
  const canSwipe = images.length > 1;
  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === images.length - 1;
  const currentImage = images[currentIndex];
  const isZoomed = zoom.level > 100;

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isZoomed) return;
      touchStartX.current = e.touches[0].clientX;
      touchEndX.current = null;
    },
    [isZoomed],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (isZoomed) return;
      touchEndX.current = e.touches[0].clientX;
    },
    [isZoomed],
  );

  const handleTouchEnd = useCallback(() => {
    if (isZoomed) return;
    if (touchStartX.current === null || touchEndX.current === null) return;

    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0 && !isLastImage) {
        onNext();
      } else if (diff < 0 && !isFirstImage) {
        onPrev();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [isZoomed, isFirstImage, isLastImage, onNext, onPrev]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft" && !isFirstImage) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && !isLastImage) {
        e.preventDefault();
        onNext();
      }
    },
    [isFirstImage, isLastImage, onPrev, onNext],
  );

  useEffect(() => {
    if (containerRef.current && zoom.level === 100) {
      containerRef.current.scrollTop = 0;
      containerRef.current.scrollLeft = 0;
    }
  }, [zoom.level]);

  if (!currentImage) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          <span className={styles["placeholder-text"]}>No floor plan available</span>
        </div>
      </div>
    );
  }

  const imageSrc = currentImage.path || currentImage.base_path;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={styles.container}
      onKeyDown={handleKeyDown}
      onTouchStart={canSwipe ? handleTouchStart : undefined}
      onTouchMove={canSwipe ? handleTouchMove : undefined}
      onTouchEnd={canSwipe ? handleTouchEnd : undefined}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      role="group"
      aria-label="Floor plan image viewer"
    >
      <div
        ref={containerRef}
        className={formatClassNames(styles["scroll-container"], isZoomed && styles.zoomed)}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/click-events-have-key-events */}
        <div
          className={styles["image-wrapper"]}
          style={{
            transform: `scale(${zoom.level / 100})`,
            cursor: onImageClick ? "pointer" : undefined,
          }}
          onClick={onImageClick}
          role="button"
          tabIndex={0}
          onKeyDown={
            onImageClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onImageClick();
                  }
                }
              : undefined
          }
          aria-label="View full image"
        >
          <Image src={imageSrc} alt={altText} width="100%" height="100%" className={styles.image} />
        </div>
      </div>

      {hasMultipleImages && (
        <>
          <button
            type="button"
            className={formatClassNames(
              styles.chevron,
              styles["chevron-left"],
              isFirstImage && styles["chevron-disabled"],
            )}
            onClick={isFirstImage ? undefined : onPrev}
            aria-label="Previous floor plan"
            disabled={isFirstImage}
          >
            <ChevronLeftIcon width="24" height="24" />
          </button>

          <button
            type="button"
            className={formatClassNames(
              styles.chevron,
              styles["chevron-right"],
              isLastImage && styles["chevron-disabled"],
            )}
            onClick={isLastImage ? undefined : onNext}
            aria-label="Next floor plan"
            disabled={isLastImage}
          >
            <ChevronRightIcon width="24" height="24" />
          </button>

          <div className={styles["position-indicator"]}>
            {currentIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

export default wrapperHOC(ImageViewer, {
  componentName: "ImageViewer-FloorPlansSection-PropertyPage",
  showForChina: true,
});
